import { useState } from 'react';
import { toast } from 'sonner';
import type { IssuedKitItem, Station } from '@hcc/types';
import { useAppendEvent, type PersonApi } from '../../hooks/usePeople';
import { ApiError } from '../../lib/api';
import {
  confirmRowsToItems,
  confirmRowsToPhotos,
  type ConfirmRow,
} from './useKitConfirmItems';

interface KitIssueSubmitInput {
  station: Station;
  person: PersonApi;
  /**
   * Idempotency key for this submission. Supplied by the draft so every
   * desk that picks the same kit up submits under one key: two of them
   * signing at once dedupes server-side instead of double-issuing.
   */
  clientEventId: string;
  onDone: () => void;
}

/**
 * The drawer's signed submit path: trims the entered items, appends one
 * KitIssued event with the signature, and translates connection failures
 * into the right operator guidance (offline queue vs retry).
 */
export function useKitIssueSubmit({
  station,
  person,
  clientEventId,
  onDone,
}: KitIssueSubmitInput) {
  const [error, setError] = useState<string | null>(null);
  const append = useAppendEvent(person.id);

  /** Returns true when the event was recorded (or offline-queued). */
  async function submit(
    items: IssuedKitItem[],
    confirmRows: ConfirmRow[],
    notes: string,
    signatureDataUrl: string,
  ): Promise<boolean> {
    setError(null);
    const cleanItems: IssuedKitItem[] = items
      .map((i) => ({
        ...i,
        name: i.name.trim(),
        size: i.size?.trim() || undefined,
        serial: i.serial?.trim() || undefined,
        registration: i.registration?.trim() || undefined,
      }))
      .filter((i) => i.name.length > 0);
    // Existing-kit rows (confirmed / not-present) ride on the same event and
    // signature; their condition photos go up as kit_photos.
    const confirmItems = confirmRowsToItems(confirmRows);
    const allItems = [...cleanItems, ...confirmItems];
    const kitPhotos = confirmRowsToPhotos(confirmRows);
    if (allItems.length === 0) {
      setError('Add at least one item before signing');
      return false;
    }
    try {
      await append.mutateAsync({
        event_type: 'KitIssued',
        payload: {
          station,
          items: allItems,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        signature_png: signatureDataUrl,
        ...(kitPhotos.length > 0 ? { kit_photos: kitPhotos } : {}),
        client_event_id: clientEventId,
      } as Parameters<typeof append.mutateAsync>[0]);
      toast.success(`${station} kit issued to ${person.full_name}`);
      onDone();
      return true;
    } catch (err) {
      // If the venue wifi blipped, the PWA service worker queues the
      // POST and replays it automatically, but ONLY when a service
      // worker actually controls this page (production build, SW
      // installed and activated). In dev, on a first visit before
      // activation, or in a private-mode browser there is no queue: a
      // "saved locally" toast would discard the event while telling
      // the operator it was recorded.
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const networkError = err instanceof TypeError && /fetch|network/i.test(err.message);
      const swControlled =
        typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller;
      if ((offline || networkError) && swControlled) {
        setError(null);
        toast.info('Saved locally — will sync to the server when the connection returns.');
        onDone();
        return true;
      }
      if (offline || networkError) {
        // Connection problem with no offline queue: keep the drawer
        // open so the entered items survive for a retry.
        setError('No connection and offline saving is unavailable. Nothing was recorded; try again when the connection returns.');
        toast.error('Not saved — no connection.');
        return false;
      }
      setError(err instanceof ApiError ? err.message : 'failed to record kit');
      toast.error('Could not record kit. Try again.');
      return false;
    }
  }

  return { submit, error };
}
